import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/auth';
import { useLanguage } from '@/context/language-context';
import { fetchEscortProfileById } from '@/services/profileService';
import { 
  Calendar, MessageSquare, Eye, Users, 
  TrendingUp, DollarSign, Clock, CheckCircle,
  ImageIcon, UserCheck
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { EscortProfile } from '@/types';
import { fetchRecentMessages } from '@/services/messageService';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

const EscortDashboardOverview: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<EscortProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    profileViews: 0,
    newMessages: 0,
    pendingBookings: 0,
    weeklyIncome: 0
  });

  useEffect(() => {
    const loadProfileData = async () => {
      if (!user || !user.escortProfileId) {
        console.warn("User or escortProfileId not available");
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        const escortProfile = await fetchEscortProfileById(user.escortProfileId);
        setProfile(escortProfile);
        
        // Simuler des statistiques (à remplacer par des vraies données)
        setStats({
          profileViews: Math.floor(Math.random() * 100) + 20,
          newMessages: Math.floor(Math.random() * 10),
          pendingBookings: Math.floor(Math.random() * 5),
          weeklyIncome: Math.floor(Math.random() * 1000) + 500
        });
      } catch (error) {
        console.error('Error loading escort profile:', error);
        toast({
          title: t('escort:dashboard.error_loading_profile'),
          description: t('escort:dashboard.try_again_later'),
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadProfileData();
  }, [user, t, toast]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('escort:dashboard.welcome')}, {profile?.displayName}</h1>
          <p className="text-muted-foreground mt-1">{t('escort:dashboard.dashboard_description')}</p>
        </div>
        <div className="mt-4 md:mt-0 flex gap-2">
          <Button variant="outline">
            {t('escort:dashboard.view_public_profile')}
          </Button>
          <Button onClick={() => navigate('/escort-dashboard/profile')}>
            {t('escort:dashboard.edit_profile')}
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex justify-between items-center">
            <div>
              <p className="text-muted-foreground text-sm">{t('escort:dashboard.profile_views')}</p>
              <p className="text-2xl font-semibold">{stats.profileViews}</p>
            </div>
            <div className="rounded-full p-3 bg-primary/10">
              <Eye className="h-5 w-5 text-primary" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex justify-between items-center">
            <div>
              <p className="text-muted-foreground text-sm">{t('escort:dashboard.new_messages')}</p>
              <p className="text-2xl font-semibold">{stats.newMessages}</p>
            </div>
            <div className="rounded-full p-3 bg-primary/10">
              <MessageSquare className="h-5 w-5 text-primary" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex justify-between items-center">
            <div>
              <p className="text-muted-foreground text-sm">{t('escort:dashboard.pending_bookings')}</p>
              <p className="text-2xl font-semibold">{stats.pendingBookings}</p>
            </div>
            <div className="rounded-full p-3 bg-primary/10">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex justify-between items-center">
            <div>
              <p className="text-muted-foreground text-sm">{t('escort:dashboard.weekly_income')}</p>
              <p className="text-2xl font-semibold">{stats.weeklyIncome}€</p>
            </div>
            <div className="rounded-full p-3 bg-primary/10">
              <DollarSign className="h-5 w-5 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Profile Status - Simplifié pour ne pas être redondant avec VisibilityTab */}
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold mb-4">{t('escort:dashboard.profile_status')}</h2>
          <div className="space-y-4">
            {/* Garder uniquement l'affichage du statut de vérification ici */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                 {/* Utiliser une icône plus appropriée comme ShieldCheck */}
                <UserCheck className={`h-5 w-5 ${profile?.isVerified ? 'text-green-500' : 'text-yellow-500'}`} /> 
                <span>{t('escort:dashboard.verification_status')}</span>
              </div>
              <span className={`font-medium ${profile?.isVerified ? 'text-green-500' : 'text-yellow-500'}`}>
                {profile?.isVerified
                  ? t('common:verified') 
                  : t('common:not_verified') // Utiliser une clé commune
                }
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-500" />
                <span>{t('escort:dashboard.availability')}</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/escort-dashboard/calendar')}>
                {t('escort:dashboard.manage_availability')}
              </Button>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-purple-500" />
                <span>{t('escort:dashboard.visibility')}</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/escort-dashboard/profile', { state: { tab: 'visibility' } })}>
                {t('escort:dashboard.boost_visibility')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold mb-4">{t('escort:dashboard.quick_actions')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Button className="flex items-center gap-2 justify-start" onClick={() => navigate('/escort-dashboard/messages')}>
              <MessageSquare className="h-5 w-5" />
              {t('escort:dashboard.view_messages')}
            </Button>
            <Button className="flex items-center gap-2 justify-start" onClick={() => navigate('/escort-dashboard/bookings')}>
              <Calendar className="h-5 w-5" />
              {t('escort:dashboard.check_bookings')}
            </Button>
            <Button className="flex items-center gap-2 justify-start" onClick={() => navigate('/escort-dashboard/media')}>
              <ImageIcon className="h-5 w-5" />
              {t('escort:dashboard.add_photos')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EscortDashboardOverview;
